import React from 'react';
import { injectIntl } from 'react-intl';
import AdminShell, { Icon } from './AdminShell';
import DataTable from './DataTable';
import { Modal, Tabs } from './Overlay';
import { Fieldset, Field, SettingRow, Switch } from './Form';
import { text } from './peopleCopy';
import { api, staticpageUploadDir, siteUrl } from '../../config';

/*
 * Static pages — layar `static` paket client.
 *
 * Tiga kolom prototipe tidak punya penyimpanan: URL (slug), Status (terbit),
 * dan tombol Add/Delete. StaticPage hanya menyimpan nama, naskah, dan meta;
 * kelima barisnya tetap dan tidak ada mutation untuk membuat atau menghapusnya.
 * Yang benar-benar ada dan justru penting — bahasa apa saja yang sudah terisi —
 * dipakai menggantikan kolom Status.
 *
 * JEBAKAN YANG PALING PENTING DI LAYAR INI: updateStaticPage MENGHAPUS SELURUH
 * baris terjemahan halaman itu lalu menulis ulang persis apa yang dikirim.
 * Menyimpan halaman dengan hanya naskah Inggris di tangan akan menghapus versi
 * Arabnya tanpa satu pun peringatan. Karena itu formulir ini selalu memuat
 * SEMUA bahasa lebih dulu dan selalu mengirim semuanya kembali, dan tombol
 * simpan mati sampai pemuatannya selesai.
 */

const gql = (query, variables) => fetch('/graphql', {
  method: 'post',
  headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
  body: JSON.stringify({ query, variables }),
  credentials: 'include'
}).then(r => r.json());

const Q = `query ($searchList: String) {
  getAdminStaticPages(searchList: $searchList) {
    count total missingArabic status errorMessage
    results {
      id pageName metaTitle metaDescription pageBanner masterLength updatedAt
      languages { language length updatedAt }
    }
  }
}`;

const Q_ONE = `query ($id: Int) {
  getEditStaticPage(id: $id) {
    status
    result {
      id pageName content metaTitle metaDescription pageBanner
      dynamicStaticContentSettings { id contentId language content }
    }
  }
}`;

const M_SAVE = `mutation ($id: Int, $content: String, $metaTitle: String,
                 $metaDescription: String, $pageBanner: String,
                 $dynamicStaticContent: [dynamicStaticContentInput]) {
  updateStaticPage(id: $id, content: $content, metaTitle: $metaTitle,
                   metaDescription: $metaDescription, pageBanner: $pageBanner,
                   dynamicStaticContent: $dynamicStaticContent) { status errorMessage }
}`;

const ok = res => res && Number(res.status) === 200;

const LANG_LABEL = { 'en-US': 'EN', en: 'EN', ar: 'AR' };
const LANG_NAME = { 'en-US': 'English', en: 'English', ar: 'العربية' };
const slugify = s => String(s || '').toLowerCase().trim().replace(/[^a-z0-9\s-]/g, '').replace(/\s+/g, '-');

const day = v => {
  const d = new Date(Number(v) || v);
  return isNaN(d) ? null : d.toISOString().slice(0, 10);
};

class StaticPages extends React.Component {
  state = {
    rows: [], count: 0, total: 0, missingArabic: 0,
    search: '', loading: true, error: null,
    // `form` diisi hanya setelah seluruh bahasa halaman itu selesai dimuat.
    form: null, loadingForm: false, tab: 0, formError: null,
    saving: false, notice: null
  };

  componentDidMount() { this.load(); }

  locale = () => this.props.intl && this.props.intl.locale;
  tx = (en, ar) => text(this.locale(), en, ar);

  load = async quiet => {
    if (!quiet) this.setState({ loading: true, error: null });
    const json = await gql(Q, { searchList: this.state.search || undefined }).catch(() => null);
    const res = json && json.data && json.data.getAdminStaticPages;
    if (!ok(res)) {
      this.setState({ loading: false, error: (res && res.errorMessage) || this.tx('Could not load static pages.', 'تعذر تحميل الصفحات الثابتة.') });
      return;
    }
    this.setState({
      rows: res.results || [], count: res.count || 0, total: res.total || 0,
      missingArabic: res.missingArabic || 0, loading: false
    });
  };

  setSearch = search => this.setState({ search }, () => this.load());

  /*
   * Selalu ambil ulang halaman lengkap sebelum membuka formulir. Daftar tidak
   * memuat naskahnya, dan menyimpan tanpa naskah lengkap di tangan akan
   * mengosongkan halamannya.
   */
  openEdit = async row => {
    this.setState({ loadingForm: true, formError: null, notice: null, tab: 0 });
    const json = await gql(Q_ONE, { id: row.id }).catch(() => null);
    const res = json && json.data && json.data.getEditStaticPage;
    const page = res && res.result;

    if (!ok(res) || !page) {
      this.setState({
        loadingForm: false,
        notice: { tone: 'danger', text: this.tx('Could not open the page for editing. Nothing was changed.', 'تعذر فتح الصفحة للتعديل. لم يتم تغيير أي شيء.') }
      });
      return;
    }

    const langs = {};
    (page.dynamicStaticContentSettings || []).forEach(d => {
      langs[d.language] = { id: d.id, content: d.content || '' };
    });

    this.setState({
      loadingForm: false,
      form: {
        id: page.id,
        pageName: page.pageName,
        content: page.content || '',
        metaTitle: page.metaTitle || '',
        metaDescription: page.metaDescription || '',
        pageBanner: page.pageBanner || '',
        langs,
        // Urutan bahasa yang ADA saat dibuka. Dipakai untuk mengirim semuanya
        // kembali, termasuk yang tidak disentuh.
        langOrder: Object.keys(langs)
      }
    });
  };

  setForm = (key, value) => this.setState(st => ({ form: { ...st.form, [key]: value } }));

  setLang = (language, content) => this.setState(st => ({
    form: {
      ...st.form,
      langs: { ...st.form.langs, [language]: { ...(st.form.langs[language] || {}), content } },
      langOrder: st.form.langOrder.indexOf(language) >= 0
        ? st.form.langOrder
        : st.form.langOrder.concat(language)
    }
  }));

  submit = async () => {
    const f = this.state.form;
    if (!f.metaTitle.trim()) {
      this.setState({ formError: this.tx('A meta title is required — it is the browser tab text.', 'عنوان التعريف مطلوب — وهو نص علامة تبويب المتصفح.') });
      return;
    }
    this.setState({ saving: true, formError: null });

    /*
     * SEMUA bahasa dikirim, bukan hanya yang disunting. Mutation ini menghapus
     * seluruh baris terjemahan halaman lalu membuat ulang dari daftar ini;
     * bahasa yang tidak ikut terkirim akan hilang dari database.
     */
    const dynamicStaticContent = f.langOrder.map(language => ({
      contentId: f.id,
      language,
      content: (f.langs[language] && f.langs[language].content) || ''
    }));

    const json = await gql(M_SAVE, {
      id: f.id,
      content: f.content,
      metaTitle: f.metaTitle.trim(),
      metaDescription: f.metaDescription,
      pageBanner: f.pageBanner || undefined,
      dynamicStaticContent
    }).catch(() => null);
    const res = json && json.data && json.data.updateStaticPage;
    this.setState({ saving: false });

    if (!ok(res)) {
      this.setState({ formError: (res && res.errorMessage) || this.tx('Could not save the page.', 'تعذر حفظ الصفحة.') });
      return;
    }
    this.setState({
      form: null,
      notice: {
        tone: 'info',
        text: `${f.pageName} ${this.tx('saved in', 'تم حفظها بـ')} ${dynamicStaticContent.length} ${this.tx('language(s).', 'لغة.')}`
      }
    });
    this.load(true);
  };

  columns() {
    const t = this.tx;
    return [
      { key: 'pageName', label: t('Page', 'الصفحة'), sortable: true, render: r => <b>{r.pageName}</b> },
      {
        key: 'metaTitle', label: t('Browser title', 'عنوان المتصفح'),
        render: r => r.metaTitle
          ? <span title={r.metaTitle}>{r.metaTitle}</span>
          : <span className="tag danger"><i />{t('Missing', 'مفقود')}</span>
      },
      {
        // Prototipe menaruh Status di sini. Tidak ada kolom terbit di tabelnya;
        // yang benar-benar menentukan sebuah halaman terbaca oleh pembaca Arab
        // adalah ada tidaknya baris bahasanya.
        key: 'languages', label: t('Languages', 'اللغات'),
        render: r => {
          const has = (r.languages || []).filter(l => l.length > 0).map(l => l.language);
          const arabic = has.some(l => l === 'ar');
          return (
            <span style={{ display: 'inline-flex', gap: 5, flexWrap: 'wrap' }}>
              {has.map(l => (
                <span className={`tag ${l === 'ar' ? 'amber' : ''}`} key={l}>
                  <i />{LANG_LABEL[l] || l}
                </span>
              ))}
              {!arabic && <span className="tag danger"><i />{t('No Arabic', 'لا توجد نسخة عربية')}</span>}
            </span>
          );
        }
      },
      {
        key: 'masterLength', label: t('Length', 'الطول'), align: 'end', sortable: true,
        render: r => r.masterLength
          ? <span className="num">{r.masterLength.toLocaleString('en-US')} {t('chars', 'حرف')}</span>
          : <span className="tag danger"><i />{t('Empty', 'فارغ')}</span>
      },
      {
        key: 'updatedAt', label: t('Updated', 'آخر تحديث'), align: 'end', sortable: true,
        render: r => <span className="mono">{day(r.updatedAt) || '—'}</span>
      },
      {
        key: 'act', label: '', align: 'end',
        render: r => (
          <span style={{ display: 'inline-flex', gap: 6 }}>
            <a className="btn icon" title={t('Preview page', 'معاينة الصفحة')}
              aria-label={`${t('Preview', 'معاينة')} ${r.pageName}`} href={`/${slugify(r.pageName)}`} target="_blank" rel="noopener noreferrer">
              <Icon name="eye" />
            </a>
            <button className="btn icon" type="button" title={t('Edit page', 'تعديل الصفحة')}
              aria-label={`${t('Edit', 'تعديل')} ${r.pageName}`}
              disabled={this.state.loadingForm}
              onClick={() => this.openEdit(r)}>
              <Icon name="edit" />
            </button>
          </span>
        )
      }
    ];
  }

  renderForm() {
    const f = this.state.form;
    const { saving, formError, tab } = this.state;
    const langs = f.langOrder;
    const labels = [this.tx('English', 'الإنجليزية')].concat(langs.filter(l => l !== 'en-US' && l !== 'en').map(l => LANG_NAME[l] || l));
    const t = this.tx;
    const slug = slugify(f.pageName);

    return (
      <Modal
        icon="doc" wide
        title={`${t('Edit', 'تعديل')} ${f.pageName}`}
        sub={t('Content is HTML. It is served as-is on the website.', 'المحتوى HTML ويُعرض كما هو على الموقع.')}
        confirmLabel={t('Save page', 'حفظ الصفحة')}
        busy={saving}
        disabled={!f.metaTitle.trim()}
        onConfirm={this.submit}
        onClose={() => this.setState({ form: null, formError: null })}
      >
        {formError && (
          <div className="notice danger">
            <span className="ni">!</span><span className="nt">{formError}</span>
          </div>
        )}

        {/* Bukan sekadar catatan: mutation ini menghapus lalu menulis ulang
            seluruh terjemahan halaman. Pembacanya perlu tahu kenapa semua
            bahasa ikut terkirim meski cuma satu yang disunting. */}
        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t(`Saving rewrites every language of this page at once. All ${langs.length} language${langs.length === 1 ? '' : 's'} loaded here are sent back together, so nothing is dropped by editing only one of them.`, `يحفظ هذا جميع لغات الصفحة دفعة واحدة. يتم إرسال اللغات المحملة هنا وعددها ${langs.length} معاً حتى لا تُفقد لغة عند تعديل أخرى.`)}
          </span>
        </div>

        <Tabs labels={labels} active={tab} onChange={i => this.setState({ tab: i })} />

        <div style={{ marginTop: 'var(--sp-4)' }}>
          <Fieldset title={t('Page details', 'تفاصيل الصفحة')}>
            <Field label={t('Title (English)', 'العنوان (الإنجليزية')} required>
              <input className="inp" value={f.pageName} readOnly />
            </Field>
            <Field label={t('URL slug', 'مسار الرابط')}>
              <div className="input-prefix"><span>{siteUrl}/</span><input className="inp mono" value={slug} readOnly /></div>
            </Field>
          </Fieldset>
          {tab === 0 && (
            <Fieldset title={t('Master content', 'المحتوى الأساسي')}
              desc={t('The base copy stored on the page row itself.', 'النص الأساسي المحفوظ في صف الصفحة.') }>
              <Field label={t('Content (HTML)', 'المحتوى (HTML)')} span>
                <textarea className="inp" rows={12} value={f.content}
                  onChange={e => this.setForm('content', e.target.value)} />
              </Field>
            </Fieldset>
          )}

          {langs.filter(l => l !== 'en-US' && l !== 'en').map((l, i) => tab === i + 1 && (
            <Fieldset key={l} title={`${LANG_LABEL[l] || l} ${t('content', 'المحتوى')}`}
              desc={l === 'ar'
                ? t('Arabic version. Shown to readers with the site in Arabic.', 'النسخة العربية. تظهر للقراء عند استخدام الموقع بالعربية.')
                : t('Shown to readers in this language.', 'تظهر للقراء بهذه اللغة.') }>
              <Field label={t('Content (HTML)', 'المحتوى (HTML)')} span>
                <textarea className="inp" rows={12} dir={l === 'ar' ? 'rtl' : 'ltr'}
                  value={(f.langs[l] && f.langs[l].content) || ''}
                  onChange={e => this.setLang(l, e.target.value)} />
              </Field>
            </Fieldset>
          ))}
        </div>

        <Fieldset title={t('Page banner', 'صورة الصفحة')}>
          {f.pageBanner
            ? <div><img src={`${api.apiEndpoint}${staticpageUploadDir}large_${f.pageBanner}`} alt={f.pageName} style={{ maxWidth: '100%', maxHeight: 180, objectFit: 'cover', borderRadius: 8 }} /><div className="hint mono">{f.pageBanner}</div></div>
            : <span className="tag danger"><i />{t('No banner image', 'لا توجد صورة')}</span>}
        </Fieldset>

        <SettingRow label={t('Publish immediately', 'النشر فوراً')}
          hint={t('Static pages are always served; publishing cannot be switched off here.', 'الصفحات الثابتة تُعرض دائماً؛ لا يمكن إيقاف النشر من هنا.') }>
          <Switch checked label={t('Publish immediately', 'النشر فوراً')} disabled onChange={() => {}} />
        </SettingRow>

        <Fieldset title={t('Search engine and tab', 'محرك البحث وعلامة التبويب')}>
          <Field label={t('Browser title', 'عنوان المتصفح')} required
            hint={t('Shown in the browser tab and in search results.', 'يظهر في علامة تبويب المتصفح ونتائج البحث.') }>
            <input className="inp" value={f.metaTitle}
              onChange={e => this.setForm('metaTitle', e.target.value)} />
          </Field>
          <Field label={t('Meta description', 'وصف التعريف')}
            hint={t('The grey line under the title in search results.', 'السطر الرمادي أسفل العنوان في نتائج البحث.') }>
            <input className="inp" value={f.metaDescription}
              onChange={e => this.setForm('metaDescription', e.target.value)} />
          </Field>
        </Fieldset>
      </Modal>
    );
  }

  render() {
    const {
      rows, count, total, missingArabic, loading, error,
      form, loadingForm, saving, notice
    } = this.state;
    const t = this.tx;

    return (
      <AdminShell
        current="static"
        breadcrumb={[t('Content', 'المحتوى'), t('Static pages', 'الصفحات الثابتة')]}
        title={t('Static pages', 'الصفحات الثابتة')}
        lead={this.locale() === 'ar' ? `${total} صفحات قانونية ودعم تُعرض على الموقع.` : `${total} legal and support page${total === 1 ? '' : 's'} served on the website.`}
        actions={[
          <a className="btn ghost" href="/siteadmin/staticpage/manage" key="c">{t('Open in classic', 'فتح النسخة الكلاسيكية')}</a>
        ]}
      >
        {notice && (
          <div className={`notice ${notice.tone}`}>
            <span className="ni">{notice.tone === 'danger' ? '!' : '✓'}</span>
            <span className="nt">{notice.text}</span>
            <button className="btn ghost sm" type="button"
              onClick={() => this.setState({ notice: null })}>Dismiss</button>
          </div>
        )}

        <div className="notice">
          <span className="ni">i</span>
          <span className="nt">
            {t('These five pages are fixed: there is no mutation to create or delete one, and no URL or publish state is stored, so those cannot be changed here. What can be edited is the content in each language and the browser title.', 'هذه الصفحات الخمس ثابتة؛ لا توجد عملية لإنشاء أو حذف صفحة، ولا يتم حفظ الرابط أو حالة النشر. يمكن تعديل المحتوى بكل لغة وعنوان المتصفح.')}
          </span>
        </div>

        {!loading && !error && missingArabic > 0 && (
          <div className="notice danger">
            <span className="ni">!</span>
            <span className="nt">
              {this.locale() === 'ar' ? `${missingArabic} من أصل ${total} صفحات لا تملك نسخة عربية. سيستخدم القراء النص الإنجليزي في هذه الصفحات.` : `${missingArabic} of ${total} pages have no Arabic version. Readers with the site in Arabic fall back to the English text on those pages.`}
            </span>
          </div>
        )}

        {loadingForm && (
          <div className="notice">
            <span className="ni">i</span>
            <span className="nt">{t('Loading every language of that page…', 'جارٍ تحميل كل لغات الصفحة…')}</span>
          </div>
        )}

        {form && this.renderForm()}

        {error
          ? <div className="notice danger"><span className="ni">!</span><span className="nt">{error}</span></div>
          : (
            <DataTable
              columns={this.columns()}
              rows={rows}
              count={count}
              page={1}
              perPage={25}
              loading={loading}
              onPage={() => {}}
              onSearch={this.setSearch}
              searchPlaceholder={t('Search page name or title', 'ابحث عن اسم الصفحة أو العنوان')}
              emptyText={t('No static pages match', 'لا توجد صفحات ثابتة مطابقة')}
              emptyHint={t('Try clearing the search.', 'جرّب مسح البحث.')}
            />
          )}
      </AdminShell>
    );
  }
}

export default injectIntl(StaticPages);
